import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { Link } from "react-router-dom";
import { Footer } from "@/components/layout/Footer";
import { MetaTags } from "@/components/seo/MetaTags";

const Privacy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags 
        title="Privacy Policy - Ritual"
        description="Learn how Ritual collects, uses, and protects your personal information. Your privacy matters to us."
      />
      
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/">
            <Logo size="sm" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">Back</Button>
          </Link>
        </div>
      </nav>

      <main className="flex-1">
        <div className="container max-w-3xl py-8 sm:py-16 px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Privacy Policy</h1>
          
          <div className="prose prose-gray dark:prose-invert max-w-none space-y-6 text-sm sm:text-base">
            <section>
              <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
              <p>
                This Privacy Policy describes how Ritual ("we", "us", or "our") collects, uses, and shares 
                information about you when you use our website, mobile applications, and services (collectively, the "Services").
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Information We Collect</h2>
              
              <h3 className="text-base font-semibold mt-4 mb-2">Information You Provide</h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>Account information (name, email address, password)</li>
                <li>Organization and team information</li>
                <li>Content you create (SOPs, tasks, proof attachments)</li>
                <li>Communications with us (support requests, feedback)</li>
              </ul>
              
              <h3 className="text-base font-semibold mt-4 mb-2">Information Collected Automatically</h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>Device information (browser type, operating system)</li>
                <li>Usage data (pages visited, features used, time spent)</li>
                <li>Log data (IP address, access times, referring URLs)</li>
                <li>Location data (country/region based on IP)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>Provide, maintain, and improve our Services</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
                <li>Personalize and improve your experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Data Sharing</h2>
              <p className="text-muted-foreground mb-4">
                We do not sell your personal information. We may share information in the following circumstances:
              </p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>With your organization's other members as part of the Services</li>
                <li>With service providers who assist in our operations</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and prevent fraud</li>
                <li>With your consent or at your direction</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your information for as long as your account is active or as needed to provide you 
                Services. We will also retain and use your information to comply with legal obligations, 
                resolve disputes, and enforce our agreements.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Data Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. This includes 
                encryption of data in transit and at rest, regular security assessments, and access controls.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Your Rights</h2>
              <p className="text-muted-foreground mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2">
                <li>Access and receive a copy of your data</li>
                <li>Correct inaccurate information</li>
                <li>Delete your personal information</li>
                <li>Object to or restrict certain processing</li>
                <li>Data portability</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">International Transfers</h2>
              <p className="text-muted-foreground">
                Your information may be transferred to and processed in countries other than your country of 
                residence. These countries may have data protection laws that are different from those in your 
                country. We take appropriate safeguards to require that your personal information remains protected.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our Services are not directed to children under 16. We do not knowingly collect personal 
                information from children under 16. If you learn that a child has provided us with personal 
                information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new policy on this page and updating the "Last updated" date. We encourage you to 
                review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold mt-8 mb-4">Contact Us</h2>
              <p className="text-muted-foreground">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:{" "}
                <a href="mailto:privacy@ritual.app" className="text-primary hover:underline">
                  privacy@ritual.app
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Privacy;
